import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { ChevronDown, ChevronUp, Download, Search, X } from 'lucide-react';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/solid';

const DiseaseClassificationsSection = () => {
    const [classificationsData, setClassificationsData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedChapter, setSelectedChapter] = useState(null);
    const [chapterData, setChapterData] = useState(null);
    const [isLoadingChapter, setIsLoadingChapter] = useState(false);
    const [chapterError, setChapterError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState({
        diseases: [],
        genes: []
    });

    useEffect(() => {
        const fetchClassificationsData = async () => {
            try {
                setIsLoading(true);
                const response = await fetch('/api/disease-classifications');
                const data = await response.json();

                if (data.success) {
                    // Filter and sort data to focus on major chapters
                    const filteredData = data.data.filter(item =>
                        item.disease_count >= 5 && item.gene_count >= 5 &&
                        !isNaN(parseInt(item.chapter.replace(/^0+/, '')))
                    );
                    filteredData.sort((a, b) => b.gene_count - a.gene_count);
                    setClassificationsData(filteredData);
                } else {
                    setError(data.message || 'Failed to load disease classification data');
                }
            } catch (err) {
                setError(`Error: ${err.message}`);
            } finally {
                setIsLoading(false);
            }
        };

        fetchClassificationsData();
    }, []);

    useEffect(() => {
        document.title = 'Internation Classification of Diseases'; // Set the page title
    }, []);

    useEffect(() => {
        // Fetch detailed data for selected chapter
        if (selectedChapter) {
            const fetchChapterData = async () => {
                try {
                    setIsLoadingChapter(true);
                    setChapterError(null);
                    const response = await fetch(`/api/disease-classifications/chapter/${selectedChapter}`);
                    const data = await response.json();

                    if (data.success) {
                        setChapterData(data.data);
                    } else {
                        setChapterError(data.message || `Failed to load data for chapter ${selectedChapter}`);
                    }
                } catch (err) {
                    setChapterError(`Error: ${err.message}`);
                } finally {
                    setIsLoadingChapter(false);
                }
            };

            fetchChapterData();
            // Reset search when changing chapters
            setSearchQuery('');
            setSearchResults({
                diseases: [],
                genes: []
            });
        }
    }, [selectedChapter]);

    // Function to perform comprehensive search
    const performSearch = (query) => {
        if (!chapterData) return;
        
        const lowerCaseQuery = query.toLowerCase().trim();
        setSearchQuery(query);
        
        if (!lowerCaseQuery) {
            // If search is empty, show all original data
            setSearchResults({
                diseases: [],
                genes: []
            });
            return;
        }
        
        // Find matching diseases
        const matchingDiseases = chapterData.diseases.filter(disease => {
            return (
                (disease.name && disease.name.toLowerCase().includes(lowerCaseQuery)) ||
                (disease.omim_id !== undefined && disease.omim_id !== null && disease.omim_id.toString().includes(lowerCaseQuery)) ||
                (disease.description && disease.description.toLowerCase().includes(lowerCaseQuery)) ||
                (disease.inheritance && disease.inheritance.toLowerCase().includes(lowerCaseQuery)) ||
                (disease.icd_code && disease.icd_code.toLowerCase().includes(lowerCaseQuery))
            );
        });
        
        // Find all genes that match, whether they're in matching diseases or not
        const allGenes = [];
        chapterData.diseases.forEach(disease => {
            disease.genes.forEach(gene => {
                if (!allGenes.some(g => g.entrez_id === gene.entrez_id)) {
                    allGenes.push(gene);
                }
            });
        });
        
        const matchingGenes = allGenes.filter(gene => {
            return (
                (gene.gene_name && gene.gene_name.toLowerCase().includes(lowerCaseQuery)) ||
                (gene.entrez_id !== undefined && gene.entrez_id !== null && gene.entrez_id.toString().includes(lowerCaseQuery)) ||
                (gene.full_name && gene.full_name.toLowerCase().includes(lowerCaseQuery)) ||
                (gene.alias && Array.isArray(gene.alias) && gene.alias.some(a => a && a.toLowerCase().includes(lowerCaseQuery))) ||
                (gene.location && gene.location.toLowerCase().includes(lowerCaseQuery))
            );
        });
        
        // For each matching gene, add all diseases that contain it
        const diseasesByGene = [];
        if (matchingGenes.length > 0) {
            chapterData.diseases.forEach(disease => {
                if (disease.genes && Array.isArray(disease.genes) && 
                    disease.genes.some(g => g && matchingGenes.some(mg => mg && mg.entrez_id === g.entrez_id))) {
                    if (!diseasesByGene.some(d => d && d.omim_id === disease.omim_id) && 
                        !matchingDiseases.some(d => d && d.omim_id === disease.omim_id)) {
                        diseasesByGene.push(disease);
                    }
                }
            });
        }
        
        setSearchResults({
            diseases: [...matchingDiseases, ...diseasesByGene],
            genes: matchingGenes
        });
    };

    // Function to handle download
    const handleDownloadCSV = () => {
        if (selectedChapter) {
            window.location.href = `/api/disease-classifications/chapter/${selectedChapter}/csv`;
        }
    };

    // Close chapter details panel
    const closeChapterDetails = () => {
        setSelectedChapter(null);
        setChapterData(null);
        setSearchQuery('');
        setSearchResults({
            diseases: [],
            genes: []
        });
    };

    if (isLoading) {
        return (
            <div className="py-12 bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
                        <p className="mt-4 text-gray-500">Loading disease classification data...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="py-12 bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="bg-red-50 p-4 rounded-md">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-9v4a1 1 0 11-2 0v-4a1 1 0 112 0zm0-4a1 1 0 11-2 0 1 1 0 012 0z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <h3 className="text-sm font-medium text-red-800">Error loading data</h3>
                                <div className="mt-2 text-sm text-red-700">{error}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <section className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">International Classification of Diseases</h1>
                    <p className="mt-2 text-gray-600">Synaptopathies Across Body Systems</p>
                </div>

                {/* Chart Section */}
                <div className="bg-white p-6 rounded-lg shadow-lg mb-8">
                    <h3 className="text-xl font-medium text-gray-900 mb-4">Distribution of Synaptic Gene Mutations by ICD-11 Chapter</h3>
                    <p className="text-gray-600 mb-6">
                        This chart shows the distribution of genes with mutations across different body systems according to the ICD-11 classification.
                        Click on a bar to see detailed information for that chapter.
                    </p>

                    <ResponsiveContainer width="100%" height={400}>
                        <BarChart
                            data={classificationsData}
                            margin={{ top: 20, right: 30, left: 20, bottom: 70 }}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                                dataKey="chapter"
                                angle={-45}
                                textAnchor="end"
                                height={70}
                                tickFormatter={(value) => `Ch. ${value}`}
                            />
                            <YAxis
                                label={{ value: 'Number', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }}
                            />
                            <Tooltip
                                formatter={(value, name) => [value, name]}
                                labelFormatter={(chapter) => {
                                    const item = classificationsData.find(d => d.chapter === chapter);
                                    return item ? `Chapter ${chapter}: ${item.name}` : `Chapter ${chapter}`;
                                }}
                            />
                            <Legend />
                            <Bar
                                dataKey="gene_count"
                                name="Genes"
                                fill="#4299e1"
                                onClick={(data) => setSelectedChapter(data.chapter)}
                                cursor="pointer"
                            />
                            <Bar
                                dataKey="disease_count"
                                name="Diseases"
                                fill="#9f7aea"
                                onClick={(data) => setSelectedChapter(data.chapter)}
                                cursor="pointer"
                            />
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                {/* Chapter Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    {classificationsData.slice(0, 4).map((chapter) => (
                        <div
                            key={chapter.chapter}
                            className="bg-white p-6 shadow rounded-lg border-l-4 border-blue-500 hover:shadow-md transition-shadow cursor-pointer"
                            onClick={() => setSelectedChapter(chapter.chapter)}
                        >
                            <h3 className="text-xl font-medium text-gray-800">ICD-11 Chapter {chapter.chapter}: {chapter.name}</h3>
                            <p className="mt-2 text-gray-600">
                                {chapter.description}
                            </p>
                            <div className="mt-4 flex justify-between items-center">
                                <div className="flex space-x-4">
                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-50 text-blue-700">
                                        <span className="font-semibold mr-1">{chapter.gene_count}</span> genes
                                    </span>
                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-purple-50 text-purple-700">
                                        <span className="font-semibold mr-1">{chapter.disease_count}</span> diseases
                                    </span>
                                </div>
                                <ChevronDown className="h-5 w-5 text-gray-400" />
                            </div>
                        </div>
                    ))}
                </div>

                {/* View All Chapters button */}
                <div className="text-center mb-12">
                    <button
                        className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        onClick={() => {
                            document.getElementById('all-chapters')?.scrollIntoView({ behavior: 'smooth' });
                        }}
                    >
                        View All Chapters
                        <ChevronDown className="ml-2 h-4 w-4" />
                    </button>
                </div>

                {/* Chapter Details Modal */}
                {selectedChapter && (
                    <div className="fixed inset-0 overflow-y-auto z-50 flex items-center justify-center">
                        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={closeChapterDetails}></div>
                        <div className="relative bg-white rounded-lg max-w-5xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col shadow-xl">
                            {/* Header */}
                            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                                {isLoadingChapter ? (
                                    <h2 className="text-xl font-semibold text-gray-900">Loading chapter details...</h2>
                                ) : chapterData ? (
                                    <div>
                                        <h2 className="text-xl font-semibold text-gray-900">
                                        ICD-11 Chapter {chapterData.chapter}: {chapterData.name}
                                        </h2>
                                        <p className="text-sm text-gray-500 mt-1">{chapterData.specific_chapter}</p>
                                    </div>
                                ) : (
                                    <h2 className="text-xl font-semibold text-gray-900">
                                        ICD-11 Chapter {selectedChapter} Details
                                    </h2>
                                )}
                                <button
                                    className="text-gray-400 hover:text-gray-500 focus:outline-none"
                                    onClick={closeChapterDetails}
                                >
                                    <X className="h-6 w-6" />
                                </button>
                            </div>

                            {/* Content */}
                            <div className="flex-1 overflow-y-auto p-6">
                                {isLoadingChapter ? (
                                    <div className="flex justify-center items-center h-64">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                                    </div>
                                ) : chapterError ? (
                                    <div className="bg-red-50 p-4 rounded-md">
                                        <div className="flex">
                                            <div className="flex-shrink-0">
                                                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-9v4a1 1 0 11-2 0v-4a1 1 0 112 0zm0-4a1 1 0 11-2 0 1 1 0 012 0z" clipRule="evenodd" />
                                                </svg>
                                            </div>
                                            <div className="ml-3">
                                                <h3 className="text-sm font-medium text-red-800">Error loading chapter details</h3>
                                                <div className="mt-2 text-sm text-red-700">{chapterError}</div>
                                            </div>
                                        </div>
                                    </div>
                                ) : chapterData ? (
                                    <div>
                                        <div className="bg-blue-50 p-4 rounded-md mb-6">
                                            <p className="text-blue-700">{chapterData.description}</p>
                                            <div className="mt-3 flex items-center space-x-6">
                                                <div className="text-sm">
                                                    <span className="text-gray-500">Diseases:</span>
                                                    <span className="ml-2 font-medium">{chapterData.disease_count}</span>
                                                </div>
                                                <div className="text-sm">
                                                    <span className="text-gray-500">Genes:</span>
                                                    <span className="ml-2 font-medium">{chapterData.gene_count}</span>
                                                </div>
                                                <button
                                                    onClick={handleDownloadCSV}
                                                    className="inline-flex items-center px-3 py-1 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                                                >
                                                    <Download className="h-4 w-4 mr-1" />
                                                    Download CSV
                                                </button>
                                            </div>
                                        </div>

                                        {/* Search input for diseases and genes */}
                                        <div className="relative mb-6">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <Search className="h-5 w-5 text-gray-400" />
                                            </div>
                                            <input
                                                type="text"
                                                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                placeholder="Search diseases, genes, and all related fields..."
                                                value={searchQuery}
                                                onChange={(e) => performSearch(e.target.value)}
                                            />
                                            {searchQuery && (
                                                <button
                                                    className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer text-gray-400 hover:text-gray-600"
                                                    onClick={() => performSearch('')}
                                                >
                                                    <X className="h-5 w-5" />
                                                </button>
                                            )}
                                        </div>

                                        {/* Search results summary */}
                                        {searchQuery && (
                                            <div className="mb-6 flex flex-wrap gap-2">
                                                <span className="text-sm text-gray-600">
                                                    Found {searchResults.diseases.length} diseases and {searchResults.genes.length} genes matching "{searchQuery}"
                                                </span>
                                                {searchResults.genes.length > 0 && (
                                                    <div className="flex flex-wrap gap-2 mt-2 w-full">
                                                        <span className="text-sm text-gray-600 mr-2">Matching genes:</span>
                                                        {searchResults.genes.slice(0, 10).map(gene => (
                                                            <span key={gene.entrez_id} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                                {gene.gene_name}
                                                            </span>
                                                        ))}
                                                        {searchResults.genes.length > 10 && (
                                                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                                                +{searchResults.genes.length - 10} more
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Disease list */}
                                        <h3 className="text-lg font-medium text-gray-900 mb-4">Diseases in this Chapter</h3>
                                        {!searchQuery ? (
                                            // Show all diseases when not searching
                                            <div className="space-y-4">
                                                {chapterData.diseases.map((disease) => (
                                                    <ChapterDiseaseItem
                                                        key={disease.omim_id}
                                                        disease={disease}
                                                        highlightText={null}
                                                    />
                                                ))}
                                            </div>
                                        ) : searchResults.diseases.length > 0 ? (
                                            // Show search results
                                            <div className="space-y-4">
                                                {searchResults.diseases.map((disease) => (
                                                    <ChapterDiseaseItem
                                                        key={disease.omim_id}
                                                        disease={disease}
                                                        highlightText={searchQuery}
                                                    />
                                                ))}
                                            </div>
                                        ) : (
                                            // Show no results message
                                            <div className="text-center py-10 text-gray-500">
                                                No diseases found matching "{searchQuery}"
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="text-center py-10 text-gray-500">
                                        No data available for this chapter
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* All Chapters Section */}
                <div id="all-chapters" className="mt-12">
                    <h3 className="text-2xl font-semibold text-gray-800 mb-6">All ICD-11 Chapters</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {classificationsData.map((chapter) => (
                            <div
                                key={chapter.chapter}
                                className="bg-white p-5 shadow rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                                onClick={() => setSelectedChapter(chapter.chapter)}
                            >
                                <div className="flex items-center justify-between">
                                    <h4 className="text-base font-medium text-gray-900">ICD-11 Chapter {chapter.chapter}</h4>
                                    <div className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full">
                                        {chapter.gene_count} genes
                                    </div>
                                </div>
                                <p className="mt-2 text-sm text-gray-500 line-clamp-2">{chapter.name}</p>
                                <div className="mt-3 flex justify-between items-center">
                                    <div className="text-xs bg-purple-100 text-purple-800 text-xs font-medium px-2 py-1 rounded-full">
                                        {chapter.disease_count} diseases
                                    </div>
                                    <ChevronDown className="h-4 w-4 text-gray-400" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

// Component for displaying individual diseases in a chapter
const ChapterDiseaseItem = ({ disease, highlightText }) => {
    const [expanded, setExpanded] = useState(false);
  
    // Auto-expand items that match search criteria
    useEffect(() => {
        if (highlightText) {
            setExpanded(true);
        }
    }, [highlightText]);

    // Function to highlight matching text
    const highlightMatches = (text, highlight) => {
        if (!highlight || !text) return text;
        
        try {
            const parts = text.toString().split(new RegExp(`(${highlight})`, 'gi'));
            return parts.map((part, i) => {
                if (!part) return null;
                return part.toLowerCase() === highlight.toLowerCase() 
                    ? <span key={i} className="bg-blue-200">{part}</span> 
                    : part;
            });
        } catch (error) {
            // If any error occurs during highlighting, return the original text
            console.log("Error in highlighting:", error);
            return text;
        }
    };
  
    // Check if this disease contains matching genes
    const hasMatchingGenes = highlightText && disease.genes && Array.isArray(disease.genes) && 
        disease.genes.some(gene => gene && (
            (gene.gene_name && gene.gene_name.toLowerCase().includes(highlightText.toLowerCase())) ||
            (gene.entrez_id !== undefined && gene.entrez_id !== null && 
             gene.entrez_id.toString().includes(highlightText.toLowerCase()))
        ));

    return (
        <div className={`bg-white border rounded-lg shadow-sm overflow-hidden ${highlightText ? 'border-blue-300' : 'border-gray-200'}`}>
            <div
                className={`px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-gray-50 ${highlightText && hasMatchingGenes ? 'bg-blue-50' : ''}`}
                onClick={() => setExpanded(!expanded)}
            >
                <div>
                    <h4 className="text-base font-medium text-gray-900 line-clamp-1">
                        {highlightText ? highlightMatches(disease.name, highlightText) : disease.name}
                    </h4>
                    <div className="flex items-center mt-1">
                        <a
                            href={`https://omim.org/entry/${disease.omim_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:text-blue-800 mr-2"
                            onClick={(e) => e.stopPropagation()}
                        >
                                                                        OMIM: {disease.omim_id !== undefined ? disease.omim_id : 'N/A'}<ArrowTopRightOnSquareIcon className="h-3 w-3 text-blue-500 ml-1 mb-1 inline" />
                        </a>
                        <span className="text-xs text-gray-500">
                            {disease.gene_count !== undefined ? disease.gene_count : disease.genes?.length || 0} {(disease.gene_count || (disease.genes?.length || 0)) === 1 ? 'gene' : 'genes'}
                            {hasMatchingGenes && highlightText && (
                                <span className="ml-2 text-blue-600">• contains matching genes</span>
                            )}
                        </span>
                    </div>
                </div>
                <div className="ml-4">
                    {expanded ? (
                        <ChevronUp className="h-5 w-5 text-gray-400" />
                    ) : (
                        <ChevronDown className="h-5 w-5 text-gray-400" />
                    )}
                </div>
            </div>

            {expanded && (
                <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
                    <h5 className="text-sm font-medium text-gray-700 mb-2">Associated Genes:</h5>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                        {disease.genes.map((gene) => {
                            const isMatching = highlightText && (
                                (gene.gene_name && gene.gene_name.toLowerCase().includes(highlightText.toLowerCase())) ||
                                (gene.entrez_id !== undefined && gene.entrez_id !== null && 
                                 gene.entrez_id.toString().includes(highlightText.toLowerCase()))
                            );
                            
                            return (
                                <div 
                                    key={gene.entrez_id} 
                                    className={`border rounded p-2 text-sm ${isMatching ? 'bg-blue-50 border-blue-300' : 'bg-white border-gray-200'}`}
                                >
                                    <a
                                        href={`/gene-search?q=${encodeURIComponent(gene.gene_name)}`}
                                        className="font-medium text-blue-600 hover:text-blue-800 italic"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {highlightText ? highlightMatches(gene.gene_name, highlightText) : gene.gene_name}
                                    </a>
                                    <div className="mt-1 text-xs text-gray-500">
                                        <a
                                            href={`https://www.ncbi.nlm.nih.gov/gene/${gene.human_entrez || gene.entrez_id}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="hover:text-blue-600"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            Human Entrez: {gene.human_entrez !== undefined ? (
                                            highlightText ? highlightMatches(gene.human_entrez.toString(), highlightText) : gene.human_entrez
                                        ) : 'N/A'}
                                            <ArrowTopRightOnSquareIcon className="h-3 w-3 text-blue-500 ml-1 mb-1 inline" />
                                        </a>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default DiseaseClassificationsSection;